/**
 * Felix Form — <felix-form>
 *
 * JSON submit transport for the felix/form block. Wraps a real <form> whose
 * fields the block composes, renders a Cloudflare Turnstile widget when a site
 * key is present, holds any submit until the widget's token exists, and posts the
 * fields as JSON so the endpoint's real answer (204 / 403 / 409 / 422 / 429) can
 * be read and shown — unlike <felix-async-form>, whose no-cors post is opaque.
 * A 409 (email already entered) shows success without tracking: Lucky keeps the
 * first entry, so a repeat is not a new capture.
 *
 * Markup contract (see blocks/form/render.php):
 *   <felix-form data-action data-form-name data-success-event data-page-name
 *               data-turnstile-sitekey data-turnstile-action data-turnstile-required
 *               data-msg-error data-msg-rate data-msg-turnstile data-msg-closed>
 *     <form action method="post" novalidate>
 *       …fields… <p data-field-error="<name>" hidden></p> …
 *       <input name="felix_hp">            honeypot, stripped before send
 *       <div data-turnstile-mount></div>   present only when a site key resolved
 *       <div data-submit-wrap><button type="submit"></button></div>
 *     </form>
 *     <div data-form-success hidden>…</div>
 *     <p data-form-error hidden></p>
 *   </felix-form>
 *
 * Turnstile contract (Linear ER-3213 / ER-3200): the token is sent as
 * `cf_turnstile_response` (underscores; the widget's own hidden input is not read
 * by Lucky), the widget carries the block's action string, no request is sent
 * until the success callback fires, and the widget is reset after EVERY attempt
 * because a token is single-use. The button stays enabled throughout: a click
 * that beats the token is held and sent when the token arrives. If the widget
 * errors or api.js cannot load, the button is disabled and the verification
 * message shown until a token arrives — never a silent bypass.
 */

import { collectAttribution, hashEmail } from '../shared/attribution.js'

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const TURNSTILE_TIMEOUT = 10000
const PHONE_MESSAGE = 'Enter a valid phone number, with the country code if outside North America.'

// North American by default: 10 digits (optionally prefixed with 1), area code
// and exchange both start 2-9. Any other country needs its + code and 8-15
// digits. Returns E.164 or null.
const normalizePhone = (value) => {
	const compact = value.replace(/[\s().-]/g, '')
	if (compact.startsWith('+') && !compact.startsWith('+1')) {
		return /^\+[2-9]\d{7,14}$/.test(compact) ? compact : null
	}
	const digits = compact.replace(/^\+/, '')
	const national = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits
	return /^[2-9]\d{2}[2-9]\d{6}$/.test(national) ? `+1${national}` : null
}

let turnstileLoader = null

const loadTurnstile = () => {
	if (window.turnstile) return Promise.resolve(window.turnstile)
	if (turnstileLoader) return turnstileLoader

	turnstileLoader = new Promise((resolve, reject) => {
		const timer = setTimeout(() => reject(new Error('turnstile-timeout')), TURNSTILE_TIMEOUT)
		const script = document.createElement('script')
		script.src = TURNSTILE_SRC
		script.async = true
		script.defer = true
		script.onload = () => {
			clearTimeout(timer)
			window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile-missing'))
		}
		script.onerror = () => {
			clearTimeout(timer)
			reject(new Error('turnstile-blocked'))
		}
		document.head.appendChild(script)
	})

	return turnstileLoader
}

class FelixForm extends HTMLElement {
	connectedCallback() {
		this.form = this.querySelector('form')
		this.action = this.dataset.action || this.form?.action
		if (!this.form || !this.action) return

		this.success = this.querySelector('[data-form-success]')
		this.error = this.querySelector('[data-form-error]')
		this.submitWrap = this.form.querySelector('[data-submit-wrap]')
		this.submitButton = this.form.querySelector('[type="submit"]')
		this.mount = this.form.querySelector('[data-turnstile-mount]')

		this.turnstileRequired = this.dataset.turnstileRequired === '1' && !!this.dataset.turnstileSitekey && !!this.mount
		this.token = null
		this.widgetId = null
		this.turnstileFailed = false
		this.pendingSubmit = false

		this.onSubmit = (event) => this.submit(event)
		this.onInput = (event) => this.clearFieldError(event.target)
		this.form.addEventListener('submit', this.onSubmit)
		this.form.addEventListener('input', this.onInput)

		this.setSubmitEnabled(true)
		if (this.turnstileRequired) this.initTurnstile()
	}

	disconnectedCallback() {
		this.form?.removeEventListener('submit', this.onSubmit)
		this.form?.removeEventListener('input', this.onInput)
	}

	// --- Turnstile ---------------------------------------------------------

	async initTurnstile() {
		try {
			const turnstile = await loadTurnstile()
			this.widgetId = turnstile.render(this.mount, {
				sitekey: this.dataset.turnstileSitekey,
				action: this.dataset.turnstileAction || 'contest_registration',
				appearance: 'interaction-only',
				'before-interactive-callback': () => {
					this.mount.hidden = false
				},
				'after-interactive-callback': () => {
					this.mount.hidden = true
				},
				callback: (token) => {
					this.token = token
					this.turnstileFailed = false
					this.setSubmitEnabled(true)
					if (this.messageKind === 'turnstile') this.hideMessage()
					if (this.pendingSubmit) {
						this.pendingSubmit = false
						this.form.requestSubmit()
					}
				},
				'expired-callback': () => {
					this.resetTurnstile()
				},
				'error-callback': () => {
					this.failTurnstile()
					return true
				},
				'timeout-callback': () => {
					this.token = null
				},
			})
		} catch {
			this.failTurnstile()
		}
	}

	failTurnstile() {
		this.token = null
		this.turnstileFailed = true
		this.pendingSubmit = false
		this.setSubmitEnabled(false)
		this.showMessage(this.dataset.msgTurnstile, 'turnstile')
	}

	resetTurnstile() {
		this.token = null
		if (window.turnstile && this.widgetId !== null) {
			try {
				window.turnstile.reset(this.widgetId)
			} catch {
				// Widget already gone; the next render will start clean.
			}
		}
	}

	// --- UI helpers --------------------------------------------------------

	setSubmitEnabled(enabled) {
		if (this.submitButton) this.submitButton.disabled = !enabled
		this.submitWrap?.classList.toggle('custom-button--disabled', !enabled)
	}

	showMessage(text, kind = 'server') {
		if (!this.error) return
		this.messageKind = kind
		this.error.textContent = text || ''
		this.error.hidden = !text
	}

	hideMessage() {
		this.messageKind = null
		if (this.error) this.error.hidden = true
	}

	fieldFor(name) {
		return this.form.querySelector(`[name="${name}"]`)
	}

	setFieldError(name, text) {
		const input = this.fieldFor(name)
		const slot = this.form.querySelector(`[data-field-error="${name}"]`)
		if (!input || !slot) return false
		slot.textContent = text
		slot.hidden = false
		input.setAttribute('aria-invalid', 'true')
		input.setAttribute('aria-describedby', slot.id)
		input.closest('.form-input')?.classList.add('form-input--error')
		return true
	}

	clearFieldError(input) {
		if (!input || !input.name) return
		const slot = this.form.querySelector(`[data-field-error="${input.name}"]`)
		if (slot) {
			slot.textContent = ''
			slot.hidden = true
		}
		input.removeAttribute('aria-invalid')
		input.removeAttribute('aria-describedby')
		input.closest('.form-input')?.classList.remove('form-input--error')
	}

	clearAllFieldErrors() {
		this.form.querySelectorAll('[aria-invalid="true"]').forEach((input) => this.clearFieldError(input))
	}

	// --- Submit ------------------------------------------------------------

	async submit(event) {
		event.preventDefault()

		this.clearAllFieldErrors()
		if (!this.validate()) return

		// Honeypot: bots fill hidden fields, people can't see them. Show fake
		// success rather than silently ignoring, so bots don't retry.
		const honeypot = this.form.querySelector('[name="felix_hp"]')
		if (honeypot && honeypot.value) {
			this.showSuccess()
			return
		}

		if (this.turnstileRequired && !this.token) {
			if (this.turnstileFailed) {
				this.showMessage(this.dataset.msgTurnstile, 'turnstile')
			} else {
				this.pendingSubmit = true
			}
			return
		}

		const payload = {}
		new FormData(this.form).forEach((value, key) => {
			if (key === 'felix_hp' || key === 'cf-turnstile-response') return
			const trimmed = typeof value === 'string' ? value.trim() : value
			payload[key] = trimmed && this.fieldFor(key)?.type === 'tel' ? normalizePhone(trimmed) : trimmed
		})
		if (this.token) payload.cf_turnstile_response = this.token

		this.setSubmitEnabled(false)
		this.hideMessage()
		this.form.setAttribute('aria-busy', 'true')

		let succeeded = false
		try {
			const response = await fetch(this.action, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify(payload),
			})
			succeeded = await this.handleResponse(response, payload)
		} catch {
			this.showMessage(this.dataset.msgError)
		} finally {
			this.form.removeAttribute('aria-busy')
			if (!succeeded) {
				// A token is single-use: every attempt needs a fresh one. A retry
				// that beats it is held like the first click.
				if (this.turnstileRequired) this.resetTurnstile()
				this.setSubmitEnabled(true)
			}
		}
	}

	validate() {
		this.form.querySelectorAll('input[type="tel"]').forEach((input) => {
			const value = input.value.trim()
			input.setCustomValidity(value && !normalizePhone(value) ? PHONE_MESSAGE : '')
		})
		const invalid = Array.from(this.form.querySelectorAll('input:not([name="felix_hp"])')).filter((input) => !input.checkValidity())
		if (invalid.length === 0) return true
		invalid.forEach((input) => {
			if (!this.setFieldError(input.name, input.validationMessage)) {
				input.setAttribute('aria-invalid', 'true')
				input.closest('.form-input')?.classList.add('form-input--error')
			}
		})
		invalid[0].focus()
		return false
	}

	async handleResponse(response, payload) {
		if (response.ok || response.status === 409) {
			if (response.ok) await this.track(payload)
			this.showSuccess()
			this.resetTurnstile()
			return true
		}

		let body = null
		try {
			body = await response.json()
		} catch {
			body = null
		}

		switch (response.status) {
			case 422: {
				let placed = false
				if (body && typeof body === 'object') {
					Object.keys(body).forEach((key) => {
						if (key === 'error' || key === 'message') return
						const text = Array.isArray(body[key]) ? body[key][0] : String(body[key])
						if (this.setFieldError(key, text)) placed = true
					})
				}
				if (!placed) this.showMessage((body && (body.error || body.message)) || this.dataset.msgError)
				const first = this.form.querySelector('[aria-invalid="true"]')
				if (first) first.focus()
				break
			}
			case 403: {
				const code = body && body.code ? String(body.code) : ''
				this.showMessage(code.indexOf('turnstile') === 0 ? this.dataset.msgTurnstile : this.dataset.msgClosed)
				break
			}
			case 429:
				this.showMessage(this.dataset.msgRate)
				break
			default:
				this.showMessage(this.dataset.msgError)
		}
		return false
	}

	async track(payload) {
		const successEvent = this.dataset.successEvent
		if (!successEvent) return
		const props = collectAttribution()
		if (this.dataset.pageName) props.pageName = this.dataset.pageName
		if (this.dataset.formName) props.formName = this.dataset.formName
		const emailField = this.form.querySelector('input[type="email"]')
		const emailHash = await hashEmail(emailField ? emailField.value : payload.email)
		if (emailHash) props.emailHash = emailHash
		window.felixTrack?.(successEvent, props)
	}

	showSuccess() {
		this.hideMessage()
		this.form.hidden = true
		if (this.success) {
			this.success.hidden = false
			// Focus would otherwise strand inside the hidden form.
			this.success.focus()
		}
	}
}

if (!customElements.get('felix-form')) {
	customElements.define('felix-form', FelixForm)
}
